import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { delftBlue } from "@/theme/colors";
import ActivityStatsCards from "./components/ActivityStatsCards";
import ActivityTable from "./components/ActivityTable";
import ActivitiesPagination from "./components/ActivitiesPagination";
import CollapsibleSection from "./components/CollapsibleSection";
import {
  useGetActivitiesQuery,
  useGetActivityStatsQuery,
  useUpdateActivityStatusMutation,
  useDeleteActivityMutation,
} from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const ActivitiesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");

  const {
    data: activitiesResponse,
    isLoading: isLoadingActivities,
    error: activitiesError,
    refetch: refetchActivities,
  } = useGetActivitiesQuery({ page, limit, search });

  const {
    data: statsResponse,
    isLoading: isLoadingStats,
    error: statsError,
    refetch: refetchStats,
  } = useGetActivityStatsQuery();

  // Refetch data when navigating back from form page
  useEffect(() => {
    if (location.state?.refresh) {
      refetchActivities();
      refetchStats();
      // Clear the state to prevent refetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, refetchActivities, refetchStats, navigate, location.pathname]);

  const [updateActivityStatus, { isLoading: isUpdating }] =
    useUpdateActivityStatusMutation();
  const [deleteActivity, { isLoading: isDeleting }] =
    useDeleteActivityMutation();

  // Check if user can add activities (Super Admin only)
  const canAddActivity = user?.role === Roles.SUPER_ADMIN;

  // Extract data from API response
  const activitiesData = activitiesResponse?.data || {
    activities: [],
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: limit,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };

  const statsData = statsResponse?.data || {
    total: 0,
    active: 0,
    inactive: 0,
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page
  };

  const handleView = (activity) => {
    navigate(`/activities/${activity._id || activity.id}/edit`);
  };

  const handleEdit = (activity) => {
    navigate(`/activities/${activity._id || activity.id}/edit`);
  };

  const handleAddActivity = () => {
    navigate("/activities/add");
  };

  const handleActivate = async (activity) => {
    try {
      await updateActivityStatus({
        id: activity._id || activity.id,
        status: !activity.isActive,
      }).unwrap();
      refetchActivities();
    } catch (error) {
      console.error("Failed to update activity status:", error);
      alert(error?.data?.message || "Failed to update activity status");
    }
  };

  const handleDelete = async (activity) => {
    if (
      !window.confirm(`Are you sure you want to delete "${activity.name}"?`)
    ) {
      return;
    }
    try {
      await deleteActivity(activity._id || activity.id).unwrap();
      refetchActivities();
    } catch (error) {
      console.error("Failed to delete activity:", error);
      alert(error?.data?.message || "Failed to delete activity");
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ alignSelf: "stretch" }}>
        {/* Header Section */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 331.6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 22, sm: 24, md: 28 },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.29,
                width: "100%",
              }}
            >
              Activities
            </Typography>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Manage and monitor all activities
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards */}
        <CollapsibleSection
          title="Activity Statistics"
          defaultExpanded={true}
          showCard={false}
        >
          {isLoadingStats ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : statsError ? (
            <Alert severity="error">
              {statsError?.data?.message || "Failed to load statistics"}
            </Alert>
          ) : (
            <ActivityStatsCards
              stats={statsData}
              onAddActivity={canAddActivity ? handleAddActivity : undefined}
            />
          )}
        </CollapsibleSection>

        {/* Activities Table */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, sm: 4, md: 6 },
          }}
        >
          {isLoadingActivities ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : activitiesError ? (
            <Alert severity="error">
              {activitiesError?.data?.message || "Failed to load activities"}
            </Alert>
          ) : (
            <>
              <ActivityTable
                activities={activitiesData.activities}
                onView={handleView}
                onEdit={
                  user?.role === Roles.SUPER_ADMIN ? handleEdit : undefined
                }
                onActivate={
                  user?.role === Roles.SUPER_ADMIN ? handleActivate : undefined
                }
                onDelete={
                  user?.role === Roles.SUPER_ADMIN ? handleDelete : undefined
                }
                isLoading={isUpdating || isDeleting}
              />

              {/* Pagination */}
              <ActivitiesPagination
                page={activitiesData.pagination?.currentPage || page}
                limit={limit}
                total={activitiesData.pagination?.totalItems || 0}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default ActivitiesPage;
